import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Receipt, Plus, FilePdf, Trash, X, Eye, Phone as PhoneIcon } from "@phosphor-icons/react";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-600",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-600",
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState({
    company_name: "", candidate_name: "", job_title: "",
    placement_salary: "", fee_percentage: "15", vat_rate: "20", notes: "",
  });

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    try {
      const { data } = await api.get("/invoices");
      setInvoices(data.items || []);
    } catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.company_name || !form.placement_salary) { toast.error("Company and salary are required"); return; }
    try {
      await api.post("/invoices", {
        ...form,
        placement_salary: parseFloat(form.placement_salary),
        fee_percentage: parseFloat(form.fee_percentage || "15"),
        vat_rate: parseFloat(form.vat_rate || "20"),
      });
      toast.success("Invoice created");
      setShowCreate(false);
      setForm({ company_name: "", candidate_name: "", job_title: "", placement_salary: "", fee_percentage: "15", vat_rate: "20", notes: "" });
      loadInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Failed to generate PDF"); }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}/status`, { status: newStatus });
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, status: newStatus } : inv));
      if (showDetail?.id === invoiceId) setShowDetail(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch { toast.error("Failed to update status"); }
  };

  const handleDelete = async (invoiceId) => {
    if (!confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      setShowDetail(null);
      loadInvoices();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 skeleton-shimmer rounded-lg" />)}</div>;

  return (
    <div data-testid="invoices-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Invoices</h1>
          <p className="text-sm text-[#6B7280] mt-1">Generate and manage placement invoices</p>
          <a href="tel:02045850050" className="text-xs text-[#6B7280] flex items-center gap-1 mt-1 hover:text-[#0F172A]"><PhoneIcon size={11} className="text-[#C5A059]" /> 020 4585 0050</a>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="create-invoice-btn">
          <Plus size={16} /> New Invoice
        </Button>
      </div>

      {invoices.length === 0 ? (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
          <Receipt size={48} className="text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-[#6B7280]">No invoices created yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left text-xs font-semibold text-[#6B7280] uppercase px-4 py-3">Invoice</th>
                <th className="text-left text-xs font-semibold text-[#6B7280] uppercase px-4 py-3 hidden md:table-cell">Company</th>
                <th className="text-left text-xs font-semibold text-[#6B7280] uppercase px-4 py-3 hidden md:table-cell">Fee</th>
                <th className="text-left text-xs font-semibold text-[#6B7280] uppercase px-4 py-3 hidden lg:table-cell">VAT</th>
                <th className="text-left text-xs font-semibold text-[#6B7280] uppercase px-4 py-3 hidden lg:table-cell">Total</th>
                <th className="text-left text-xs font-semibold text-[#6B7280] uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-[#6B7280] uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setShowDetail(inv)} data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-[#0F172A]">{inv.invoice_number}</p>
                    <p className="text-xs text-[#6B7280]">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ""}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-sm text-[#374151]">{inv.company_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-sm font-medium text-[#0F172A]">{"\u00a3"}{(inv.fee_amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-sm text-[#6B7280]">{"\u00a3"}{(inv.vat_amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-sm font-semibold text-[#C5A059]">{"\u00a3"}{(inv.total_amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${STATUS_COLORS[inv.status] || "bg-gray-100"}`}>{inv.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleDownloadPdf(inv.id)} className="text-[#1E3A8A] hover:text-[#0F172A] mr-2" title="Download PDF" data-testid={`download-pdf-${inv.id}`}>
                      <FilePdf size={18} />
                    </button>
                    <button onClick={() => handleDelete(inv.id)} className="text-red-400 hover:text-red-600" title="Delete">
                      <Trash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4" data-testid="create-invoice-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Create Invoice</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Company Name" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="invoice-company-input" />
              <Input placeholder="Candidate Name" value={form.candidate_name} onChange={e => setForm({...form, candidate_name: e.target.value})} data-testid="invoice-candidate-input" />
              <Input placeholder="Job Title" value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} data-testid="invoice-job-input" />
              <Input type="number" placeholder="Placement Salary" value={form.placement_salary} onChange={e => setForm({...form, placement_salary: e.target.value})} data-testid="invoice-salary-input" />
              <Input type="number" placeholder="Fee %" value={form.fee_percentage} onChange={e => setForm({...form, fee_percentage: e.target.value})} data-testid="invoice-fee-input" />
              <Input type="number" placeholder="VAT Rate %" value={form.vat_rate} onChange={e => setForm({...form, vat_rate: e.target.value})} data-testid="invoice-vat-input" />
              {form.placement_salary && (() => {
                const salary = parseFloat(form.placement_salary || 0);
                const feePct = parseFloat(form.fee_percentage || 15);
                const vatPct = parseFloat(form.vat_rate || 20);
                const fee = salary * feePct / 100;
                const vat = fee * vatPct / 100;
                const total = fee + vat;
                return (
                  <div className="p-3 bg-[#F0F4FF] rounded-lg text-sm space-y-1" data-testid="invoice-preview-calc">
                    <p className="text-[#374151]">Net Fee: <span className="font-medium text-[#0F172A]">{"\u00a3"}{fee.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span></p>
                    <p className="text-[#374151]">VAT ({vatPct}%): <span className="font-medium text-[#0F172A]">{"\u00a3"}{vat.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span></p>
                    <p className="text-[#1E3A8A] font-semibold border-t border-[#D1D5DB] pt-1 mt-1">Total: {"\u00a3"}{total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
                  </div>
                );
              })()}
              <textarea className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm resize-none" rows={2} placeholder="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              <Button onClick={handleCreate} className="w-full bg-[#0F172A] hover:bg-[#1E293B]" data-testid="submit-invoice-btn">Create Invoice</Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4" data-testid="invoice-detail-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">{showDetail.invoice_number}</h2>
              <button onClick={() => setShowDetail(null)}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-[#6B7280]">Company</p><p className="font-medium">{showDetail.company_name || "N/A"}</p></div>
                <div><p className="text-xs text-[#6B7280]">Candidate</p><p className="font-medium">{showDetail.candidate_name || "N/A"}</p></div>
                <div><p className="text-xs text-[#6B7280]">Job Title</p><p className="font-medium">{showDetail.job_title || "N/A"}</p></div>
                <div><p className="text-xs text-[#6B7280]">Salary</p><p className="font-medium">{"\u00a3"}{(showDetail.placement_salary || 0).toLocaleString()}</p></div>
                <div><p className="text-xs text-[#6B7280]">Fee %</p><p className="font-medium">{showDetail.fee_percentage}%</p></div>
                <div><p className="text-xs text-[#6B7280]">Net Fee</p><p className="font-medium">{"\u00a3"}{(showDetail.fee_amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p></div>
                <div><p className="text-xs text-[#6B7280]">VAT ({showDetail.vat_rate || 20}%)</p><p className="font-medium">{"\u00a3"}{(showDetail.vat_amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p></div>
                <div><p className="text-xs text-[#6B7280]">Total (inc. VAT)</p><p className="font-semibold text-[#C5A059]">{"\u00a3"}{(showDetail.total_amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p></div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs text-[#6B7280]">Status:</span>
                {["draft", "sent", "paid", "overdue"].map(s => (
                  <button key={s} onClick={() => handleStatusChange(showDetail.id, s)}
                    className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded transition-colors ${showDetail.status === s ? STATUS_COLORS[s] : "bg-[#F3F4F6] text-[#9CA3AF] hover:bg-[#E5E7EB]"}`}
                    data-testid={`status-${s}`}>{s}</button>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => handleDownloadPdf(showDetail.id)} className="flex-1 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 gap-2" data-testid="detail-download-pdf">
                  <FilePdf size={16} /> Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
